import { cloneElement, useState } from 'react'
import { Controller, FieldPath, FieldValues, UseControllerProps } from 'react-hook-form'
import { Combobox, useCombobox } from '@mantine/core'

import { Color, Colors } from '../style/colors'
import { TrackingLabel } from '../types/tracking'
import { WithClassName } from '../types/utils'
import { Checkbox } from './Checkbox'
import { Chip } from './Chip'
import { ChipSelectableOptionProp } from './ChipListInput'
import { Icon } from './Icon'
import { BaseInput, BaseInputProps } from './Input'

// the `select all` row is a regular combobox option, told apart from the real values by this sentinel
const SELECT_ALL_VALUE = 'select-all'

type MultiSelectOption = ChipSelectableOptionProp<string>

type MultiSelectFieldProps = WithClassName &
  Omit<BaseInputProps, 'value' | 'onChange'> & {
    values: string[]
    selectableOptions: MultiSelectOption[]
    onChange: (values: string[]) => void
    chipColor?: Color
    /** plural name of what is being selected, used by the `Select all <x>` row and the collapsed chip */
    entityLabel?: string
    displayLabel?: boolean
    searchPlaceholder?: string
    /** used by the `Select all <x>` row and the chips, the per-option label comes from the option itself */
    trackingLabel: TrackingLabel
    /** when set, the parent owns the search: `selectableOptions` is taken as already filtered and is never filtered again here */
    onSearchChange?: (search: string) => void
    /** caps how many rows the dropdown renders, the rest are reached through the search */
    maxVisibleOptions?: number
    /** replaces the empty message while the parent is still resolving a search */
    isSearching?: boolean
    /** labels for values `selectableOptions` no longer holds — a parent-filtered list drops them as the search narrows */
    labelsByValue?: Record<string, string>
    /** called as the list is scrolled to its end, for a parent holding more pages than `selectableOptions` shows */
    onReachEnd?: () => void
    /** shows a loading row under the options while the parent appends the next page */
    isLoadingMore?: boolean
    /** holds the chips back while the parent is still resolving `labelsByValue`, so no chip ever shows a raw value then flips to its label */
    isLoadingLabels?: boolean
  }

// how close to the bottom, in pixels, the list has to be scrolled before the next page is asked for
const REACH_END_THRESHOLD_PX = 40

const MultiSelectField = ({
  values,
  selectableOptions,
  onChange,
  chipColor,
  entityLabel = 'options',
  displayLabel,
  searchPlaceholder = 'Search',
  trackingLabel,
  onSearchChange,
  maxVisibleOptions,
  isSearching,
  labelsByValue,
  onReachEnd,
  isLoadingMore,
  isLoadingLabels,
  disabled,
  ...props
}: MultiSelectFieldProps) => {
  const combobox = useCombobox({ onDropdownClose: () => combobox.resetSelectedOption() })
  const [search, setSearch] = useState('')

  const selected = new Set(values)
  const enabledOptions = selectableOptions.filter((option) => !option.disabled)
  const areAllSelected = enabledOptions.length > 0 && enabledOptions.every((option) => selected.has(option.value))
  // filtering a parent-owned list again would drop the rows it matched on something other than the label
  const filteredOptions = onSearchChange || !search ? selectableOptions : selectableOptions.filter((option) => option.label.toLowerCase().includes(search.toLowerCase()))
  const visibleOptions = maxVisibleOptions === undefined ? filteredOptions : filteredOptions.slice(0, maxVisibleOptions)

  const getLabel = (value: string) => selectableOptions.find((option) => option.value === value)?.label ?? labelsByValue?.[value] ?? value

  const toggleOption = (value: string) => onChange(selected.has(value) ? values.filter((v) => v !== value) : [...values, value])

  // `select all` always applies to the whole list, never to the current search, so its state stays readable while filtering
  const toggleAll = () => onChange(areAllSelected ? [] : enabledOptions.map((option) => option.value))

  const chips = areAllSelected
    ? [{ key: SELECT_ALL_VALUE, text: `All ${entityLabel} selected (${enabledOptions.length})`, onRemove: () => onChange([]) }]
    : values.map((value, index) => ({ key: value, text: getLabel(value), onRemove: () => onChange(values.filter((_, i) => i !== index)) }))

  const input = (
    <BaseInput
      {...props}
      disabled={disabled}
      readOnly
      value=""
      autoComplete="off"
      className={disabled ? '[&>div:first-child]:bg-ghost!' : undefined}
      iconRight={combobox.dropdownOpened ? 'chevron_up' : 'chevron_down'}
      isClearable={false}
      internalInputRenderer={(innerInput: any) => (
        <div className="flex flex-wrap items-center gap-1.5 py-2.5">
          {isLoadingLabels && chips.length > 0 ? (
            <span className="italic color-slate-grey">
              Loading {values.length} {entityLabel}...
            </span>
          ) : chips.length > 0 ? (
            chips.map((chip) => <Chip key={chip.key} text={chip.text} color={chipColor} iconRight={{ name: 'close', clickable: { onClick: chip.onRemove, trackingLabel } }} />)
          ) : (
            <span style={{ fontStyle: 'italic', color: Colors.SlateGrey }}>{props.label}</span>
          )}
          {cloneElement(innerInput, { style: { width: 0, minWidth: 0, padding: 0, flex: 'none', border: 'none', outline: 'none' } })}
        </div>
      )}
    />
  )

  return (
    <Combobox store={combobox} onOptionSubmit={(value) => (value === SELECT_ALL_VALUE ? toggleAll() : toggleOption(value))}>
      <Combobox.Target>
        <div className={disabled ? 'pointer-events-none cursor-not-allowed' : undefined} onClick={() => combobox.toggleDropdown()}>
          {input}
        </div>
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Search
          classNames={{
            wrapper: 'm-3',
            input: 'border border-platinum w-132 h-[46px] rounded-[3px] outline-none! text-base leading-[26px] color-jet placeholder:italic placeholder:text-(--color-slate-grey)',
          }}
          leftSectionWidth={34}
          value={search}
          onChange={(e) => {
            setSearch(e.currentTarget.value)
            onSearchChange?.(e.currentTarget.value)
          }}
          placeholder={searchPlaceholder}
          leftSection={<Icon name="search" size="md" color={Colors.SlateGrey} fill={Colors.SlateGrey} />}
        />
        <Combobox.Options
          className="max-h-50 overflow-y-auto scrollbar-thin"
          onScroll={(event) => {
            const { scrollTop, clientHeight, scrollHeight } = event.currentTarget
            if (scrollTop + clientHeight >= scrollHeight - REACH_END_THRESHOLD_PX) onReachEnd?.()
          }}
        >
          {!isSearching && visibleOptions.length === 0 && <span className="block px-3 py-2 italic color-slate-grey">No results match your search.</span>}
          {visibleOptions.map((option) => (
            <Combobox.Option key={option.value} value={option.value} disabled={option.disabled}>
              <Checkbox className="pointer-events-none" checked={selected.has(option.value)} disabled={option.disabled} label={option.label} trackingLabel={option.trackingLabel} />
            </Combobox.Option>
          ))}
          {isLoadingMore && <span className="block px-3 py-2 italic color-slate-grey">Loading more {entityLabel}...</span>}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  )
}

type MultiSelectInputProps<T extends FieldValues, U extends FieldPath<T>> = Omit<UseControllerProps<T, U>, 'defaultValue'> &
  Omit<MultiSelectFieldProps, 'values' | 'onChange'> & {
    onChange?: (values: string[]) => void
  }

export const MultiSelectInput = <T extends FieldValues, U extends FieldPath<T>>({ name, control, rules, shouldUnregister, onChange: externalOnChange, ...props }: MultiSelectInputProps<T, U>) => (
  <Controller
    control={control}
    name={name}
    rules={rules}
    shouldUnregister={shouldUnregister}
    render={({ field: { value = [], onChange } }) => (
      <MultiSelectField
        {...props}
        name={name}
        values={value as string[]}
        onChange={(next) => {
          onChange(next)
          externalOnChange?.(next)
        }}
      />
    )}
  />
)
